import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
  scaffolderApiRef,
} from '@backstage/plugin-scaffolder-react';
import { useEffect } from 'react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { ProductInfo, ProductPicker } from '../ProductPicker';
import { Box, Typography } from '@material-ui/core';
import {
  ProductMaturityConfig,
  WelcomeComponentOptions,
  welcomeSchema,
} from './schema';
import {
  getExtra,
  PreviewOptions,
  resetExtras,
  resetFormData,
  setExtra,
  setFieldPreview,
} from '../extensions';
import { Ae360Logo } from '../Ae360Logo';
import { fetchPreviousAnswer } from './previous-answers-service';
import {
  createFormPersistenceKey,
  loadStateFromUrl,
  restoreFormState,
} from '../PersistFormState/form-state-manager';
import { getFormDefaults, getTemplateRefFromPath } from '../utils.ts';

const WelcomeComponent = (props: FieldExtensionComponentProps<any>) => {
  const { onChange, uiSchema, formData: productCode } = props;
  const uiOptions: WelcomeComponentOptions = uiSchema?.['ui:options'] as any;
  const previewOptions: PreviewOptions | undefined = uiSchema['ui:preview'];

  useEffect(() => {
    resetExtras();
    onChange(productCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onProductSelect = (newProduct: ProductInfo | null) => {
    if (!newProduct) {
      onChange(undefined);
      return;
    }

    if (previewOptions) {
      setFieldPreview(previewOptions.key, {
        label: previewOptions.label,
        value: newProduct.name,
      });
    }

    setExtra('product_name', newProduct.name);
    setExtra('product_code', newProduct.code);
    setExtra('old_product', productCode);
    onChange(newProduct.code);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ gap: '1rem' }}
      alignItems="center"
    >
      <Ae360Logo />
      <Typography variant="h6"> {uiOptions?.title}</Typography>
      <ProductPicker
        onValueChange={onProductSelect}
        productCode={productCode}
        label="Product"
      />
    </Box>
  );
};

export const WelcomeComponentExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'WelcomeComponent',
    schema: welcomeSchema.schema,
    component: WelcomeComponent,
    validation: async (fieldValue, validation, context) => {
      const { apiHolder, uiSchema, formData } = context;

      if (!fieldValue) {
        validation.addError('Required');
        return;
      }

      const configApi = apiHolder.get(configApiRef);
      const identityApi = apiHolder.get(identityApiRef);
      const scaffolderApi = apiHolder.get(scaffolderApiRef);

      const template = await scaffolderApi?.getTemplateParameterSchema(
        getTemplateRefFromPath(),
      );

      const uiOptions: WelcomeComponentOptions = uiSchema?.[
        'ui:options'
      ] as any;
      const previewOptions: PreviewOptions = uiSchema?.['ui:preview'] as any;

      const config: ProductMaturityConfig | undefined = configApi?.getOptional(
        'cyber.product-maturity',
      ) as ProductMaturityConfig;

      if (!configApi || !config) {
        return;
      }

      const identityEntity = await identityApi?.getBackstageIdentity();
      const templateRef = getTemplateRefFromPath();
      const key = createFormPersistenceKey(
        identityEntity,
        templateRef,
        fieldValue,
      );

      let urlState = loadStateFromUrl();
      urlState = urlState.product_name !== fieldValue ? {} : urlState;

      if (getExtra('old_product') !== fieldValue) {
        resetFormData(formData, ['product_name']);
        urlState = {};
      }

      const defaults = getFormDefaults(formData, template);
      const persistedState = restoreFormState(key);
      const result = await fetchPreviousAnswer(fieldValue, {
        options: uiOptions,
        config,
        previewOpts: previewOptions,
      });

      setExtra('store_key', key);
      Object.assign(formData, defaults, result, persistedState, urlState);
    },
  }),
);
